import { GsFormField, GsFormIf, GsFormSchema } from 'libs/shared/src/components/gs-form/interfaces/gs-form-schema';
import { PictureDisplayMode } from '../../../models/basic/media';
import { GsStructBorderRadius } from '../../../models/structure/components/component';
import { DEFAULT_CONFIG } from '../../../services/config/default.config';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { borderRadiusSchemaFields } from './../basics/border.schema';
import { buttonSchemaFields } from './../basics/button-fields.schema';
import { DISPLAY_MODE_SCHEMA } from './../basics/display-mode.schema';
import { PAGINATED_SCHEMA_FIELDS } from './../basics/paginated.schema';
import { pictureDisplayModeSchemaFor } from './../basics/picture-display-mode.schema';
import { Mode } from './../basics/swiper.schema';
import { OPENING_HOURS_SCHEMA_FIELDS } from './opening-hours.schema';
import { SPACING_SCHEMA } from './spacing.schema';

/** Condition: the picture settings only matter when the item picture is shown */
const SHOW_PICTURE_IF: GsFormIf = { args: [{ formControlName: 'showPicture', value: true }] };

/**
 * The schema for the chainstore list component form.
 * Defines the fields to display for the form.
 */
export const CHAINSTORE_LIST_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    ...BASIC_SCHEMA_FIELDS,
    ...SPACING_SCHEMA,
    ...DISPLAY_MODE_SCHEMA,
    {
      formControlName: 'showSearch',
      label: 'components.chainstoreList.schema.showSearch',
      type: 'checkbox',
      default: false,
      groupingName: 'components.chainstoreList.schema.search',
      if: {
        args: [
          {
            formControlName: 'mode',
            value: Mode.GRID
          }
        ]
      }
    },
    {
      formControlName: 'showPicture',
      label: 'components.chainstoreList.schema.showPicture',
      type: 'checkbox',
      default: true,
      groupingName: 'schema.items.name'
    },
    ...borderRadiusSchemaFields('ItemImage', GsStructBorderRadius.md, 'schema.items.name', SHOW_PICTURE_IF, 'schema.imageRadius'),
    {
      formControlName: 'itemImageHeight',
      label: 'schema.items.itemImageHeight',
      type: 'number',
      isResponsive: true,
      nullable: true,
      groupingName: 'schema.items.name',
      if: SHOW_PICTURE_IF
    },
    {
      ...pictureDisplayModeSchemaFor('pictureDisplayMode', undefined, undefined, 'schema.items.name'),
      default: PictureDisplayMode.Cut,
      if: SHOW_PICTURE_IF
    } as GsFormField<never>,
    {
      formControlName: 'showText',
      label: 'components.chainstoreList.schema.showText',
      type: 'checkbox',
      default: true,
      groupingName: 'schema.items.name'
    },
    {
      formControlName: 'showLink',
      label: 'components.chainstoreList.schema.showLink',
      type: 'checkbox',
      default: true,
      groupingName: 'schema.items.name'
    },
    {
      formControlName: 'linkText',
      label: 'components.chainstoreList.schema.linkText',
      type: 'text',
      default: '',
      groupingName: 'schema.items.name',
      if: {
        args: [
          {
            formControlName: 'showLink',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'showLinkAsButton',
      label: 'components.chainstoreList.schema.makeLinkButton',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.items.name',
      if: {
        args: [
          {
            formControlName: 'showLink',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'buttonRounded',
      label: 'components.chainstoreList.schema.makeButtonRounded',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.items.name',
      if: {
        chaining: 'and',
        args: [
          {
            formControlName: 'showLink',
            value: true
          },
          {
            formControlName: 'showLinkAsButton',
            value: true
          }
        ]
      }
    },
    //Contact details
    {
      formControlName: 'showInformation',
      label: 'components.chainstoreList.schema.showContactDetails',
      type: 'checkbox',
      default: false,
      groupingName: 'components.chainstoreList.schema.contactDetails'
    },
    {
      formControlName: 'showAddress',
      label: 'components.chainstoreList.schema.showAddress',
      type: 'checkbox',
      default: true,
      groupingName: 'components.chainstoreList.schema.contactDetails',
      if: {
        args: [
          {
            formControlName: 'showInformation',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'showPhone',
      label: 'components.chainstoreList.schema.showPhone',
      type: 'checkbox',
      default: true,
      groupingName: 'components.chainstoreList.schema.contactDetails',
      if: {
        args: [
          {
            formControlName: 'showInformation',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'showEmail',
      label: 'components.chainstoreList.schema.showEmail',
      type: 'checkbox',
      default: true,
      groupingName: 'components.chainstoreList.schema.contactDetails',
      if: {
        args: [
          {
            formControlName: 'showInformation',
            value: true
          }
        ]
      }
    },
    {
      formControlName: 'showSocialMedia',
      label: 'components.chainstoreList.schema.showSocialMedia',
      type: 'checkbox',
      default: false,
      groupingName: 'components.chainstoreList.schema.contactDetails',
      if: {
        args: [
          {
            formControlName: 'showInformation',
            value: true
          }
        ]
      }
    },
    ...OPENING_HOURS_SCHEMA_FIELDS,
    ...PAGINATED_SCHEMA_FIELDS,
    {
      formControlName: 'buttons',
      label: 'components.chainstoreList.schema.buttons',
      type: 'group',
      expandable: true,
      default: DEFAULT_CONFIG.uiElements?.buttons ?? {},
      fields: buttonSchemaFields()
    }
  ]
};
